import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  CanvasBreakdownPayload,
  CanvasBreakdownRuntime,
} from '../../core/types/payloads/canvas_breakdown.types';
import type { MusicalToken } from '../../core/types/music';
import type { ScoreRenderEvent } from '../../core/music/vex/vexFlowMapper';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';
import { normalizeFigureLegendEntries } from '../../core/music/figureLegendNormalize';

const TOKENS = new Set([
  'W',
  'H',
  'Q',
  'E',
  'S',
  'R_W',
  'R_H',
  'R_Q',
  'R_E',
  'R_S',
]);

function normalizeTokens(raw: unknown): MusicalToken[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(String).filter((t): t is MusicalToken => TOKENS.has(t));
}

function normalizeScoreEvents(raw: unknown): ScoreRenderEvent[] | undefined {
  if (!Array.isArray(raw) || !raw.length) return undefined;
  return raw as ScoreRenderEvent[];
}

function normalizeScoreEventsByBar(
  raw: unknown,
): Record<number, ScoreRenderEvent[]> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<number, ScoreRenderEvent[]> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const n = Number(key);
    if (Number.isFinite(n) && Array.isArray(value) && value.length) {
      out[n] = value as ScoreRenderEvent[];
    }
  }
  return Object.keys(out).length ? out : undefined;
}

export function validateCanvasBreakdownPayload(raw: unknown): CanvasBreakdownPayload {
  if (!raw || typeof raw !== 'object') throw new Error('CanvasBreakdown payload must be an object');
  const data = raw as Partial<CanvasBreakdownPayload>;
  const title = String(data.title || '').trim();
  const bodyText = String(data.bodyText || '').trim();
  if (!title) throw new Error('CanvasBreakdown requires title');
  if (!bodyText) throw new Error('CanvasBreakdown requires bodyText');
  const patternTokens = normalizeTokens(data.patternTokens);
  if (!patternTokens.length) throw new Error('CanvasBreakdown requires patternTokens');
  const ts = String(data.timeSignature || '4/4');
  const timeSignature =
    ts === '3/4' || ts === '2/4' || ts === '4/4' ? (ts as CanvasBreakdownPayload['timeSignature']) : '4/4';

  const phraseBarNumbers = Array.isArray(data.phraseBarNumbers)
    ? data.phraseBarNumbers.map(Number).filter((n) => Number.isFinite(n) && n >= 1)
    : undefined;
  const barTokenStarts = Array.isArray(data.barTokenStarts)
    ? data.barTokenStarts.map(Number).filter((n) => Number.isFinite(n) && n >= 0)
    : undefined;

  return {
    title,
    bodyText,
    sectionTitle: data.sectionTitle ? String(data.sectionTitle) : undefined,
    timeSignature,
    targetBarNumber:
      typeof data.targetBarNumber === 'number' && data.targetBarNumber >= 1
        ? data.targetBarNumber
        : undefined,
    patternTokens,
    rhythmDots: Array.isArray(data.rhythmDots)
      ? data.rhythmDots.map(Number).filter((n) => Number.isFinite(n) && n >= 0)
      : [],
    modifiers:
      data.modifiers && typeof data.modifiers === 'object'
        ? {
            ties: Array.isArray((data.modifiers as { ties?: unknown }).ties)
              ? ((data.modifiers as { ties: [number, number][] }).ties ?? [])
              : undefined,
            keySignature: (data.modifiers as { keySignature?: string }).keySignature
              ? String((data.modifiers as { keySignature: string }).keySignature)
              : undefined,
          }
        : undefined,
    syllableGuide: data.syllableGuide ? String(data.syllableGuide) : undefined,
    rhythmMethod:
      data.rhythmMethod === 'takadimi' || data.rhythmMethod === 'kodaly' ? data.rhythmMethod : 'tradicional',
    graphicMode: data.graphicMode === 'symbols-labels' ? 'symbols-labels' : 'symbols',
    phraseBarNumbers: phraseBarNumbers?.length ? phraseBarNumbers : undefined,
    barTokenStarts: barTokenStarts?.length ? barTokenStarts : undefined,
    scoreEvents: normalizeScoreEvents(data.scoreEvents),
    scoreEventsByBar: normalizeScoreEventsByBar(data.scoreEventsByBar),
    figureLegend: Array.isArray(data.figureLegend)
      ? data.figureLegend.map(String).filter(Boolean)
      : undefined,
    figureLegendEntries: normalizeFigureLegendEntries(data.figureLegendEntries),
    teacherScript: data.teacherScript ? String(data.teacherScript) : undefined,
  };
}

export function createInitialCanvasBreakdownRuntime(
  _payload: CanvasBreakdownPayload,
  _routing: RoutingMode,
): CanvasBreakdownRuntime {
  return { viewed: false };
}

export const canvasBreakdownLogic: ActivityLogicPlugin<'canvas_breakdown'> = {
  mode: 'canvas_breakdown',
  validatePayload: validateCanvasBreakdownPayload,
  createInitialRuntime: createInitialCanvasBreakdownRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope) {
    if (input.action === 'EXAM_CONTINUE' || input.action === 'VIEW') {
      return { runtime: { ...ctx.runtime, viewed: true } };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoCanvasBreakdownPayload(): CanvasBreakdownPayload {
  return validateCanvasBreakdownPayload({
    title: 'Demostración guiada',
    bodyText: 'Miremos juntos este compás. Percútelo siguiendo las figuras completas.',
    timeSignature: '4/4',
    targetBarNumber: 1,
    patternTokens: ['Q', 'E', 'Q', 'E'],
  });
}
